import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  Button,
} from "@fluentui/react-components";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentAvatar } from "../../../../../components/FluentUI/Fluent-Avatar";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import {
  createColumnMenu,
  formatLastUpdatedDate,
  renderlastUpdatedTooltipHeader,
  renderTooltipHeader,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks } from "../../../dashboard-util/dashboard-utils";
import TechDealTeamAvatarGroupCell from "../../tech-deal-team-avatar";
import { flattenTechProjects, getDealFilteredSortedData, TechStafferNoDealOnFilterState, TechStafferNoDealsEmptyState, SeniorMembersCell } from "../tech-staffer-helper";
import { BADGE_COLORS, computeBankerDetailExportData, handleBankerExport, BankerExcelExports } from "./tech-staffer-banker-time-log-helper";
import "../tech-staffer-time-log.scss";

const TechStafferBankerTimeLogModal = ({ show, onHide, selectedBanker }) => {
  const [dealState, setDealState] = useState({ sort: [], filter: null });
  const exportRef = useRef(null);

  const flattenedDeals = useMemo(
    () => flattenTechProjects(selectedBanker?.TechProjects || []),
    [selectedBanker]
  );

  const filteredDeals = useMemo(
    () => getDealFilteredSortedData(flattenedDeals, dealState.filter, dealState.sort),
    [flattenedDeals, dealState.filter, dealState.sort]
  );

  const detailExportData = useMemo(
    () => computeBankerDetailExportData(filteredDeals),
    [filteredDeals]
  );

  const summaryRef = useRef(null);

  const columnMenu = createColumnMenu(flattenedDeals);
  const isEmpty = filteredDeals.length === 0;
  const isFilterEmpty = isEmpty && (dealState.filter?.filters?.length ?? 0) > 0;
  const finalGridData = isEmpty ? [{ isEmptyRow: true }] : filteredDeals;

  const columnIsFiltered = useCallback(
    (field) => isFilteredColumn(field, dealState.filter),
    [dealState.filter]
  );

  const resetDataState = () => setDealState({ sort: [], filter: null });

  const canReset = dealState.sort.length > 0 || !!(dealState.filter?.filters?.length);

  const summaryData = useMemo(() => {
    if (!selectedBanker) return [];
    return [{
      GroupName: selectedBanker.GroupName,
      Banker: selectedBanker.Banker,
      LastWeekHours: selectedBanker.LastWeekHours,
      WeekendHours: selectedBanker.WeekendHours,
      TotalHours: selectedBanker.TotalHours,
      CurrentWeekHours: selectedBanker.CurrentWeekHours,
    }];
  }, [selectedBanker]);

  useEffect(() => {
    document.body.classList.toggle("tech-dialog-open", show);
    return () => document.body.classList.remove("tech-dialog-open");
  }, [show]);

  return (    <>    <Dialog
      open={show}
      modalType="modal"
      onOpenChange={(_, data) => { if (!data.open) onHide(); }}
    >
      <DialogSurface aria-describedby={undefined} className="tech-staffer-modal">
        <DialogBody>
          <DialogTitle
            className="d-flex justify-content-between"
            style={{ paddingBottom: 0 }}
          >
            <div>
              {selectedBanker?.GroupName && (
                <FluentBadge
                  shape="rounded"
                  appearance="tint"
                  size="medium"
                  color={BADGE_COLORS[selectedBanker.GroupName] || "informative"}
                  text={selectedBanker.GroupName}
                />
              )}
              <div className="modal-deal-title-text">{selectedBanker?.Banker}</div>
              <p className="subDepartmentTitle">Banker Time Log</p>
            </div>
            <div className="d-flex align-items-start">
              <FluentTooltip
                content="Reset Filters and Sorting"
                target={
                  <FluentButton
                    appearance="secondaryWithOutline"
                    icon={
                      <FluentUiIcon
                        name="ArrowResetRegular"
                        color={canReset ? "#0F6CBD" : "#A0A0A0"}
                        size={20}
                        disabled={!canReset}
                      />
                    }
                    disabled={!canReset}
                    onClick={resetDataState}
                  />
                }
              />
              <span className="ml-2">
                <FluentTooltip
                  content="Export Data"
                  target={
                    <FluentButton
                      appearance="secondaryWithOutline"
                      icon={
                        <FluentUiIcon name="ArrowDownloadRegular" color="#0F6CBD" size={20} />
                      }
                      onClick={() => handleBankerExport({ summaryRef, exportRef, selectedBanker })}
                    />
                  }
                />
              </span>
              <span className="ml-2">
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<FluentUiIcon name="DismissRegular" />}
                  />
                </DialogTrigger>
              </span>
            </div>
          </DialogTitle>

          <DialogContent>
            <div className="tech-staffer-time-log noBorder" style={{ maxWidth: "100%" }}>
              {/* Summary row */}
              <div className="summary-grid-wrapper">
                <Grid
                  data={summaryData}
                  className="tech-staffer-time-log-summary-grid"
                  resizable={true}
                  sortable={true}
                >
                  <Column
                    field="Banker"
                    title={renderTooltipHeader(selectedBanker?.GroupName ?? "Banker")}
                    columnMenu={columnMenu}
                    cell={() => (
                      <td className="summary-cell k-table-td avatar-cell">
                        <div className="dashboard-username">
                          <FluentAvatar size={28} name={selectedBanker?.Banker} className="task-member-user mr-2" />
                          <FluentTooltip
                            target={<span style={{ marginLeft: "8px" }}>{selectedBanker?.Banker}</span>}
                            text={selectedBanker?.Banker ?? ""}
                          />
                        </div>
                      </td>
                    )}
                  />
                  <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Total Last Wk Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.LastWeekHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Total Wknd Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.WeekendHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="TotalHours"
                    title={renderTooltipHeader("Total Actual Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.TotalHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="CurrentWeekHours"
                    title={renderTooltipHeader("Total Current Wk Est")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.CurrentWeekHours ?? "–"}</td>
                    )}
                  />
                </Grid>
              </div>

              {/* Deal detail grid */}
              <div className="detail-grid-scroll-container">
                <Grid
                  data={finalGridData}
                  dataItemKey="ProjectId"
                  sortable={true}
                  sort={dealState.sort}
                  filter={dealState.filter}
                  onSortChange={(e) => setDealState((s) => ({ ...s, sort: e.sort }))}
                  onFilterChange={(e) => setDealState((s) => ({ ...s, filter: e.filter }))}
                  resizable={true}
                >
                  <Column
                    field="ProjectId"
                    title={renderTooltipHeader("#")}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
                    cell={(props) =>
                      props.dataItem.isEmptyRow ? (
                        <td colSpan={9} className="k-table-td">
                          {isFilterEmpty ? (
                            <TechStafferNoDealOnFilterState resetDataState={resetDataState} />
                          ) : (
                            <TechStafferNoDealsEmptyState isStaffer={false} />
                          )}
                        </td>
                      ) : (
                        <td className="badge-edit-cell background-white k-table-td">
                          <div className="badge-edit-content" style={{ pointerEvents: "none" }}>
                            <FluentBadge
                              shape="rounded"
                              appearance="tint"
                              size="medium"
                              color="success"
                              text={`GIB-${props.dataItem.ProjectId}`}
                            />
                            {props.dataItem.IsOverDue ? (
                              <FluentBadge
                                shape="rounded"
                                appearance="filled"
                                size="medium"
                                color="warning"
                                text="Overdue"
                              />
                            ) : props.dataItem.IsNew && (
                              <FluentBadge
                                shape="rounded"
                                appearance="filled"
                                size="medium"
                                color="success"
                                text="New"
                              />
                            )}
                          </div>
                        </td>
                      )
                    }
                  />
                  <Column
                    field="ProjectName"
                    title={renderTooltipHeader("Deal Name")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <FluentTooltip
                          target={
                            <td className="background-white k-table-td">
                              <div className="project-name-wrapper">
                                <span className="project-name-text">{props.dataItem.ProjectName}</span>
                              </div>
                            </td>
                          }
                          text={props.dataItem.ProjectName ?? ""}
                        />
                      )
                    }
                  />
                  <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Last Wk Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">{props.dataItem.LastWeekHours ?? "–"}</td>
                      )
                    }
                  />
                  <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Last Wknd Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">{props.dataItem.WeekendHours ?? "–"}</td>
                      )
                    }
                  />
                  <Column
                    field="TotalHours"
                    title={renderTooltipHeader("Total Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("TotalHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">{props.dataItem.TotalHours ?? "–"}</td>
                      )
                    }
                  />
                  <Column
                    field="CurrentWeekHours"
                    title={renderTooltipHeader("Current Wk Est")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">{props.dataItem.CurrentWeekHours ?? "–"}</td>
                      )
                    }
                  />
                  <Column
                    field="Members"
                    title={renderTooltipHeader("Deal Team")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          <TechDealTeamAvatarGroupCell members={props.dataItem.Members} />
                        </td>
                      )
                    }
                  />
                  <Column
                    field="SeniorMembers"
                    title={renderTooltipHeader("MDs/Directors/VPs")}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("SeniorMembers") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          <SeniorMembersCell
                            names={(props.dataItem.SeniorMembers || [])
                              .map((m) => (typeof m === "string" ? m : m.User?.Name || m.Name))
                              .filter(Boolean)}
                          />
                        </td>
                      )
                    }
                  />
                  <Column
                    field="LastUpdated"
                    title={renderlastUpdatedTooltipHeader("Last Updated")}
                    columnMenu={columnMenu}
                    filter="date"
                    headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td
                          className="background-white k-table-td"
                          style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}
                        >
                          <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                        </td>
                      )
                    }
                  />
                </Grid>
              </div>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <BankerExcelExports
      summaryData={summaryData}
      detailExportData={detailExportData}
      summaryRef={summaryRef}
      exportRef={exportRef}
    />
  </>
  );
};

TechStafferBankerTimeLogModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedBanker: PropTypes.shape({
    Banker: PropTypes.string,
    GroupName: PropTypes.string,
    LastWeekHours: PropTypes.number,
    WeekendHours: PropTypes.number,
    TotalHours: PropTypes.number,
    CurrentWeekHours: PropTypes.number,
    TechProjects: PropTypes.array,
  }),
};

export default TechStafferBankerTimeLogModal;
